import React, { useEffect, useRef, useState } from 'react';
import { GMap } from 'primereact/gmap';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { loadGoogleMapsEmpleado } from '../DatosEmpleado/GoogleMapsEmpleado';
import { decodePolyline, encodePolyline } from '../../util/distanciaPolilinea';

/**
 * Editor visual de una ruta guardada. Devuelve al padre via onChange:
 *   { origenLabel, origenLat, origenLng,
 *     destinoLabel, destinoLat, destinoLng,
 *     polilineaEncoded }  // encoded polyline de Google
 *
 * Si `valorInicial` trae datos, los hidrata. Si trae polilineaEncoded, la dibuja
 * tal cual (respeta el ajuste manual); si no, recalcula con Directions.
 */
const DibujadorDeRutaGuardada = ({ valorInicial = null, onChange }) => {
    const [mapReady, setMapReady] = useState(false);

    const [origenLabel, setOrigenLabel] = useState('');
    const [origenPos, setOrigenPos] = useState(null); // {lat,lng}
    const [destinoLabel, setDestinoLabel] = useState('');
    const [destinoPos, setDestinoPos] = useState(null);

    const [puntos, setPuntos] = useState([]); // [{lat,lng}, ...] polilinea editable
    const [overlays, setOverlays] = useState([]);
    const [hidratado, setHidratado] = useState(false);

    const mapRef = useRef(null);
    const directionsServiceRef = useRef(null);
    const geocoderRef = useRef(null);
    const inputOrigenRef = useRef(null);
    const inputDestinoRef = useRef(null);
    const toast = useRef(null);

    // Refs paralelas al estado: el GMap monta el listener una unica vez con la
    // closure inicial; usar refs garantiza ver el estado actual en el click.
    const origenPosRef = useRef(null);
    const destinoPosRef = useRef(null);
    useEffect(() => { origenPosRef.current = origenPos; }, [origenPos]);
    useEffect(() => { destinoPosRef.current = destinoPos; }, [destinoPos]);

    useEffect(() => {
        loadGoogleMapsEmpleado(() => {
            if (!directionsServiceRef.current) {
                directionsServiceRef.current = new window.google.maps.DirectionsService();
            }
            if (!geocoderRef.current) {
                geocoderRef.current = new window.google.maps.Geocoder();
            }
            setMapReady(true);
        });
    }, []);

    /** Reverse-geocoding: aplica un label legible al callback (fallback: "lat, lng"). */
    const reverseGeocode = (pos, setLabel) => {
        const fallback = `${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}`;
        setLabel(fallback);
        if (!geocoderRef.current) return;
        geocoderRef.current.geocode({ location: pos }, (results, status) => {
            if (status === 'OK' && results && results[0]) {
                setLabel(results[0].formatted_address);
            }
        });
    };

    /** Parsea un texto como par "lat, lng" (acepta ",", espacio o parentesis). */
    const parsearCoord = (texto) => {
        if (!texto) return null;
        const m = String(texto).match(/^\s*\(?\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*\)?\s*$/);
        if (!m) return null;
        const lat = parseFloat(m[1]);
        const lng = parseFloat(m[2]);
        if (isNaN(lat) || isNaN(lng)) return null;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
        return { lat, lng };
    };

    /** Si el texto del input es una coordenada lat/lng, la aplica al origen/destino. */
    const intentarFijarPorCoord = (texto, setPos, setLabel) => {
        const coord = parsearCoord(texto);
        if (!coord) return false;
        setPos(coord);
        reverseGeocode(coord, setLabel);
        if (mapRef.current) mapRef.current.panTo(coord);
        return true;
    };

    /**
     * Click en el mapa:
     *  - sin origen: setea origen.
     *  - con origen pero sin destino: setea destino.
     *  - con ambos: reinicia con el nuevo punto como origen.
     */
    const handleMapClick = (event) => {
        if (!event?.latLng) return;
        const pos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        if (!origenPosRef.current) {
            setOrigenPos(pos);
            reverseGeocode(pos, setOrigenLabel);
        } else if (!destinoPosRef.current) {
            setDestinoPos(pos);
            reverseGeocode(pos, setDestinoLabel);
        } else {
            setOrigenPos(pos);
            reverseGeocode(pos, setOrigenLabel);
            setDestinoPos(null);
            setDestinoLabel('');
            setPuntos([]);
        }
    };

    // Hidratar desde valorInicial una unica vez tras estar listo el mapa.
    useEffect(() => {
        if (!mapReady || hidratado || !valorInicial) return;
        setOrigenLabel(valorInicial.origenLabel || '');
        setDestinoLabel(valorInicial.destinoLabel || '');
        if (valorInicial.origenLat != null && valorInicial.origenLng != null) {
            setOrigenPos({ lat: valorInicial.origenLat, lng: valorInicial.origenLng });
        }
        if (valorInicial.destinoLat != null && valorInicial.destinoLng != null) {
            setDestinoPos({ lat: valorInicial.destinoLat, lng: valorInicial.destinoLng });
        }
        if (valorInicial.polilineaEncoded) {
            const decoded = decodePolyline(valorInicial.polilineaEncoded);
            setPuntos(decoded.map(([lat, lng]) => ({ lat, lng })));
        } else if (
            valorInicial.origenLat != null && valorInicial.origenLng != null &&
            valorInicial.destinoLat != null && valorInicial.destinoLng != null
        ) {
            setPuntos([
                { lat: valorInicial.origenLat, lng: valorInicial.origenLng },
                { lat: valorInicial.destinoLat, lng: valorInicial.destinoLng }
            ]);
        }
        setHidratado(true);
    }, [mapReady, hidratado, valorInicial]);

    // Inicializar autocompletes una vez listo el mapa.
    useEffect(() => {
        if (!mapReady) return;
        const inicializarAutocomplete = (refInput, onPick) => {
            const inputElement = refInput.current?.getElement?.() || refInput.current;
            if (!inputElement || !window.google?.maps?.places) return;
            const ac = new window.google.maps.places.Autocomplete(inputElement, {
                fields: ['geometry', 'formatted_address'],
                types: ['geocode', 'establishment']
            });
            ac.addListener('place_changed', () => {
                const place = ac.getPlace();
                if (place.geometry?.location) {
                    const pos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
                    onPick(place.formatted_address || '', pos);
                }
            });
        };
        setTimeout(() => {
            inicializarAutocomplete(inputOrigenRef, (label, pos) => {
                setOrigenLabel(label);
                setOrigenPos(pos);
                if (mapRef.current) mapRef.current.panTo(pos);
            });
            inicializarAutocomplete(inputDestinoRef, (label, pos) => {
                setDestinoLabel(label);
                setDestinoPos(pos);
            });
        }, 600);
    }, [mapReady]);

    // Re-dibujar marcadores + polilinea ante cambios.
    useEffect(() => {
        if (!mapReady || !window.google) return;
        const nuevos = [];

        if (origenPos) {
            nuevos.push(new window.google.maps.Marker({
                position: origenPos,
                label: { text: 'A', color: 'white' },
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#22C55E', fillOpacity: 1,
                    strokeWeight: 2, strokeColor: 'white', scale: 11
                }
            }));
        }
        if (destinoPos) {
            nuevos.push(new window.google.maps.Marker({
                position: destinoPos,
                label: { text: 'B', color: 'white' },
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#EF4444', fillOpacity: 1,
                    strokeWeight: 2, strokeColor: 'white', scale: 11
                }
            }));
        }

        if (puntos.length > 1) {
            nuevos.push(new window.google.maps.Polyline({
                path: puntos,
                geodesic: true,
                strokeColor: '#334eff',
                strokeOpacity: 0.7,
                strokeWeight: 4
            }));
            // Cada punto draggable (excepto origen y destino, que se manejan aparte)
            puntos.forEach((p, idx) => {
                if (idx === 0 || idx === puntos.length - 1) return;
                const m = new window.google.maps.Marker({
                    position: p,
                    draggable: true,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: '#334eff', fillOpacity: 1,
                        strokeWeight: 2, strokeColor: 'white', scale: 7
                    }
                });
                m.addListener('dragend', (event) => {
                    const nuevaPos = { lat: event.latLng.lat(), lng: event.latLng.lng() };
                    setPuntos((prev) => {
                        const copia = [...prev];
                        copia[idx] = nuevaPos;
                        return copia;
                    });
                });
                nuevos.push(m);
            });
        }

        setOverlays(nuevos);
    }, [mapReady, origenPos, destinoPos, puntos]);

    // Propagar al padre.
    useEffect(() => {
        if (!onChange) return;
        const polilineaEncoded = puntos.length >= 2
            ? encodePolyline(puntos.map((p) => [p.lat, p.lng]))
            : null;
        onChange({
            origenLabel,
            origenLat: origenPos?.lat ?? null,
            origenLng: origenPos?.lng ?? null,
            destinoLabel,
            destinoLat: destinoPos?.lat ?? null,
            destinoLng: destinoPos?.lng ?? null,
            polilineaEncoded
        });
    }, [origenLabel, origenPos, destinoLabel, destinoPos, puntos, onChange]);

    const calcularRuta = () => {
        if (!origenPos || !destinoPos) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Atencion',
                detail: 'Indica origen y destino primero.'
            });
            return;
        }
        directionsServiceRef.current.route(
            {
                origin: origenPos,
                destination: destinoPos,
                travelMode: window.google.maps.TravelMode.DRIVING
            },
            (result, status) => {
                if (status === 'OK') {
                    const path = result.routes[0].overview_path;
                    setPuntos(path.map((p) => ({ lat: p.lat(), lng: p.lng() })));
                    toast.current?.show({
                        severity: 'info',
                        summary: 'Ruta calculada',
                        detail: 'Arrastra los puntos intermedios si necesitas ajustar.'
                    });
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo calcular la ruta.'
                    });
                }
            }
        );
    };

    const limpiar = () => {
        setOrigenLabel('');
        setOrigenPos(null);
        setDestinoLabel('');
        setDestinoPos(null);
        setPuntos([]);
    };

    const onMapReady = (event) => {
        mapRef.current = event.map;
    };

    const centroInicial = origenPos || destinoPos || { lat: -0.180653, lng: -78.467834 };

    if (!mapReady) return <div className="p-3 text-center text-600">Cargando mapa...</div>;

    return (
        <div className="flex flex-column gap-3">
            <Toast ref={toast} />
            <div className="formgrid grid p-fluid">
                <div className="field col-12 md:col-6">
                    <label className="font-semibold">Origen:</label>
                    <InputText
                        ref={inputOrigenRef}
                        value={origenLabel}
                        onChange={(e) => setOrigenLabel(e.target.value)}
                        onBlur={(e) => intentarFijarPorCoord(e.target.value, setOrigenPos, setOrigenLabel)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                intentarFijarPorCoord(e.target.value, setOrigenPos, setOrigenLabel);
                            }
                        }}
                        placeholder="Direccion, lugar, o coordenadas: -2.123, -79.987"
                    />
                </div>
                <div className="field col-12 md:col-6">
                    <label className="font-semibold">Destino:</label>
                    <InputText
                        ref={inputDestinoRef}
                        value={destinoLabel}
                        onChange={(e) => setDestinoLabel(e.target.value)}
                        onBlur={(e) => intentarFijarPorCoord(e.target.value, setDestinoPos, setDestinoLabel)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                intentarFijarPorCoord(e.target.value, setDestinoPos, setDestinoLabel);
                            }
                        }}
                        placeholder="Direccion, lugar, o coordenadas: -2.123, -79.987"
                    />
                </div>
                <div className="field col-12 flex gap-2">
                    <Button
                        type="button"
                        label="Calcular ruta"
                        icon="pi pi-directions"
                        className="p-button-success"
                        onClick={calcularRuta}
                        disabled={!origenPos || !destinoPos}
                    />
                    <Button
                        type="button"
                        label="Limpiar"
                        icon="pi pi-trash"
                        className="p-button-outlined p-button-danger"
                        onClick={limpiar}
                    />
                </div>
                <div className="field col-12">
                    <div className="surface-border border-2 border-round shadow-1">
                        <GMap
                            onMapReady={onMapReady}
                            options={{ center: centroInicial, zoom: 13 }}
                            overlays={overlays}
                            style={{ width: '100%', height: '380px' }}
                            onMapClick={handleMapClick}
                        />
                    </div>
                    <small className="text-500 block">
                        Click en el mapa para fijar {!origenPos ? 'el origen' : !destinoPos ? 'el destino' : 'un nuevo origen (reinicia)'}.
                        Tambien podes buscarlos por direccion arriba. Tras "Calcular ruta", arrastra los puntos intermedios para ajustar.
                    </small>
                </div>
            </div>
        </div>
    );
};

export default DibujadorDeRutaGuardada;
